import CustomChip from 'Components/CustomChip'
import NoWrapTableCell from 'Components/NoWrapTableCell'
import StyledTableHead from 'Components/StyledTableHead'
import {
  fetchTicketTransferCount,
  setMaxTransfers,
  type TransferCountShape
} from 'Services/SalesManagement/Features/AffiliateProduct/affiliateProductSlice'
import useTypedSelector, { useAppDispatch } from 'Store'
import SnackbarUtils from 'Util/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import ErrorIcon from '@mui/icons-material/ErrorOutline'
import RemoveIcon from '@mui/icons-material/Remove'
import {
  Box,
  Collapse,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { unwrapResult } from '@reduxjs/toolkit'
import { type FC, useEffect, useState } from 'react'
import { t } from 'ttag'

interface TransferCountError {
  error: string
}

interface TransferCountsProps {
  ticketId: number
}

function getInitialMaxTransfersCounts(value: TransferCountShape) {
  return value.max
}

const TransferCounts: FC<TransferCountsProps> = ({ ticketId }) => {
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const params = {
    id: ticketId
  }
  const { transferCountLoading, singleTicketTransferCount } = useTypedSelector(
    state => state.affiliateProduct
  )

  const [ticketTransferCount, setTicketTransferCount] = useState<TransferCountShape | null>(null)

  const [newMaxTransfers, setNewMaxTransfers] = useState<number>(0)

  const [buttonClickCounter, setButtonClickCounter] = useState(0)
  const [transferCountsActionLoading, setTransferCountsActionLoading] = useState(false)

  const [transferCountError, setTransferCountError] = useState<TransferCountError>()

  useEffect(() => {
    dispatch(fetchTicketTransferCount(params))
      .then(unwrapResult)
      .then(result => {
        setTicketTransferCount(result)
        setNewMaxTransfers(getInitialMaxTransfersCounts(result))
      })
      .catch(error => {
        setTransferCountError(error)
      })
  }, [])

  const handleIncrement = () => {
    setNewMaxTransfers(prevState => prevState + 1)
    setButtonClickCounter(prevState => prevState + 1)
  }

  const handleDecrement = () => {
    setNewMaxTransfers(prevState => prevState - 1)
    setButtonClickCounter(prevState => prevState - 1)
  }

  const handleCancel = () => {
    setNewMaxTransfers(
      getInitialMaxTransfersCounts(singleTicketTransferCount as TransferCountShape)
    )
    setButtonClickCounter(0)
  }

  const handleSave = () => {
    setTransferCountsActionLoading(true)
    dispatch(
      setMaxTransfers({
        id: ticketId,
        old_max: getInitialMaxTransfersCounts(singleTicketTransferCount as TransferCountShape),
        new_max: newMaxTransfers
      })
    )
      .then(unwrapResult)
      .then(() => {
        setTransferCountsActionLoading(false)
        SnackbarUtils.success(t`Max transfers successfully updated.`)
        setButtonClickCounter(0)
      })
      .catch(e => {
        console.error(e)
        SnackbarUtils.error(t`There was an error updating the max transfers.`)
        setTransferCountsActionLoading(false)
      })
  }

  const disableDecrement = newMaxTransfers <= 0
  const maxTransfersChanged = buttonClickCounter !== 0
  const isLoading = transferCountLoading === 'pending'

  return (
    <TableContainer component={Paper} sx={{ mb: 3 }}>
      <Table>
        <StyledTableHead>
          <TableRow>
            <TableCell colSpan={3}>{t`Transfer counts`}</TableCell>
          </TableRow>
        </StyledTableHead>
        {isLoading ? (
          <LinearProgress />
        ) : (
          <>
            <TableHead>
              <TableRow>
                <TableCell>{t`Transfers`}</TableCell>
                <TableCell>{t`Transfers max.`}</TableCell>
                <TableCell sx={{ textAlign: 'right' }}>{t`Manage transfers`}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ticketTransferCount ? (
                <>
                  <TableRow>
                    <TableCell>{ticketTransferCount.current}</TableCell>
                    <TableCell>{newMaxTransfers}</TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>
                      <Box>
                        <Typography fontSize={11} component="span">{t`Increment`}</Typography>
                        <IconButton
                          title={t`Increment`}
                          disabled={transferCountsActionLoading}
                          sx={{ background: theme.palette.secondary.main, margin: 0.5 }}
                          size="small"
                          onClick={handleIncrement}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          title={t`Decrement`}
                          disabled={disableDecrement || transferCountsActionLoading}
                          sx={{ background: theme.palette.secondary.main, margin: 0.5 }}
                          size="small"
                          onClick={handleDecrement}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography fontSize={11} component="span">{t`Decrement`}</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                      <Collapse in={maxTransfersChanged} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                          <Table size="small">
                            <TableBody>
                              <TableRow>
                                <TableCell
                                  colSpan={4}
                                  padding="none"
                                  component="th"
                                  scope="row"
                                  sx={{
                                    borderBottom: 'unset',
                                    minWidth: 200,
                                    textAlign: 'right'
                                  }}
                                >
                                  {transferCountsActionLoading ? (
                                    <LinearProgress />
                                  ) : (
                                    <>
                                      <CustomChip
                                        onClick={handleCancel}
                                        size="small"
                                        style={{
                                          backgroundColor: 'transparent',
                                          color: theme.palette.text.secondary,
                                          marginRight: 5
                                        }}
                                        label={t`Cancel`}
                                      />
                                      <CustomChip
                                        onClick={handleSave}
                                        size="small"
                                        label={t`Save`}
                                      />
                                    </>
                                  )}
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </>
              ) : (
                <TableRow>
                  <TableCell>{'-'}</TableCell>
                  <TableCell>{'-'}</TableCell>
                  <NoWrapTableCell sx={{ textAlign: 'right' }}>
                    <Tooltip title={transferCountError ? transferCountError.error : ''}>
                      <IconButton sx={{ padding: 0 }}>
                        <ErrorIcon sx={{ color: theme.palette.error.main }} />
                      </IconButton>
                    </Tooltip>
                  </NoWrapTableCell>
                </TableRow>
              )}
            </TableBody>
          </>
        )}
      </Table>
    </TableContainer>
  )
}

export { TransferCounts }
